<template>
    <Spinner v-if="displaySpinner" />
    <div
        class="form-container"
        v-if="!suggestions.length && !generatingSuggestions"
    >
        <div class="field">
            <label for="name">Name</label>
            <InputText
                id="name"
                autofocus
                v-model="newName"
                required
                :value="newName"
            />
        </div>
        <div class="field">
            <label for="description">Short Description</label>
            <Textarea
                id="description"
                v-model="shortDescription"
                rows="3"
                placeholder="Briefly describe this product's purpose (optional)"
                autoResize
                class="w-full"
            />
        </div>
    </div>
    <SuggestionsList
        :generatingSuggestions="generatingSuggestions"
        :suggestions="suggestions"
        :selectedSuggestions="selectedSuggestions"
        @update:selectedSuggestions="selectedSuggestions = $event"
    />
    <div class="buttons" v-if="!generatingSuggestions">
        <Button label="Cancel" text severity="secondary" @click="cancel" />
        <Button
            :label="
                suggestions.length
                    ? `Add ${selectedSuggestions.length} suggestions`
                    : 'Save'
            "
            severity="primary"
            @click="closeAndSave"
            :disabled="!!suggestions.length && !selectedSuggestions.length"
            v-tooltip="
                suggestions.length && !selectedSuggestions.length
                    ? 'Select at least one suggestion to add'
                    : ''
            "
        />
        <Button
            v-if="!suggestions.length && !generatingSuggestions"
            class="p-button-secondary"
            icon="pi pi-sparkles"
            label="Generate suggestions"
            @click="generateSuggestions"
            id="generateSuggestionsButton"
        />
    </div>
</template>

<script setup lang="ts">
import { Ref, computed, inject, ref } from "vue";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Spinner from "@/shared/Spinner.vue";
import { useProductsManager, useProductsStore } from "@/stores/products-store";
import { useHotKeys } from "@/composables/hotkeys";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import SuggestionsList from "@/components/SuggestionsList.vue";
import { useBusinessEntitiesStore } from "@/stores/business-entities-store";

const newName = ref("");
const shortDescription = ref("");
const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");
const { addHotKey } = useHotKeys();

const { create } = useProductsStore();
const { isLoading } = useProductsManager();
const { generateChildren } = useBusinessEntitiesStore();
const suggestions = ref<{ name: string | undefined; description: string }[]>(
    []
);
const selectedSuggestions = ref<number[]>([]);
const generatingSuggestions = ref<boolean>(false);

const cancel = () => dialogRef?.value.close();
const closeAndSave = async () => {
    if (newName.value) {
        // If manual entry with short description
        if (shortDescription.value) {
            const descriptionBlob = new Blob([shortDescription.value], {
                type: "text/markdown",
            });
            await create(
                newName.value,
                undefined,
                { showToast: true },
                descriptionBlob
            );
        } else {
            await create(newName.value);
        }
    } else if (selectedSuggestions.value.length) {
        // If adding from suggestions
        for (const index of selectedSuggestions.value) {
            const suggestion = suggestions.value[index];
            const descriptionBlob = new Blob([suggestion.description], {
                type: "text/markdown",
            });
            await create(
                suggestion.name || "",
                undefined,
                undefined,
                descriptionBlob
            );
        }
    }

    dialogRef?.value?.close();
};

const displaySpinner = computed(() => {
    return isLoading.value;
});

/**
 * Save on cmd/ctrl + enter.
 */
addHotKey("ControlOrMeta+Enter", async () => {
    await closeAndSave();
});

const generateSuggestions = async () => {
    generatingSuggestions.value = true;
    suggestions.value = (await generateChildren()) || [];
    generatingSuggestions.value = false;
};
</script>

<style lang="scss" scoped>
.form-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.field {
    width: 100%;

    label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
    }

    input,
    textarea {
        width: 100%;
    }
}

.buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1.5rem;
}
</style>
